import { useEffect, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { AlertCircle } from 'lucide-react';
import { login, register, phoneLogin, phoneRegister, bindPhone, bindWechatCode, fetchMe } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

interface LoginPageProps {
  onSuccess: () => void;
  onBack?: () => void;
  initialMode?: "email" | "wechat";
}

export function LoginPage({ onSuccess, onBack, initialMode = "email" }: LoginPageProps) {
  const { refreshUser } = useAuth();

  // Email form
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // Phone form
  const [phone, setPhone] = useState('');
  const [phonePassword, setPhonePassword] = useState('');
  const [phoneConfirm, setPhoneConfirm] = useState('');

  const [isRegister, setIsRegister] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // After phone login/register, if WeChat not bound, show bind step
  const [pendingUser, setPendingUser] = useState<{ phone: string | null; wechat_openid: string | null } | null>(null);

  // WeChat QR popup handler
  const [qrCheckInterval, setQrCheckInterval] = useState<ReturnType<typeof setInterval> | null>(null);

  // Bind phone dialog (for WeChat new users)
  const [bindPhoneOpen, setBindPhoneOpen] = useState(false);
  const [bindPhoneValue, setBindPhoneValue] = useState('');
  const [bindPhoneConfirm, setBindPhoneConfirm] = useState('');
  const [bindLoading, setBindLoading] = useState(false);

  // Bind WeChat button for phone-logged-in users
  const [bindWechatLoading, setBindWechatLoading] = useState(false);

  useEffect(() => {
    if (initialMode === "wechat") {
      // Small delay to ensure render is complete before opening popup
      const timer = setTimeout(() => handleWechatLogin(), 300);
      return () => clearTimeout(timer);
    }
  }, [initialMode]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlError = params.get('error');
    if (urlError) {
      setError(decodeURIComponent(urlError));
    }

    const needsPhoneBind = params.get('needs_phone_bind') === '1';
    if (needsPhoneBind) {
      setBindPhoneOpen(true);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (qrCheckInterval) clearInterval(qrCheckInterval);
    };
  }, [qrCheckInterval]);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      if (isRegister) {
        await register(email, password);
      } else {
        await login(email, password);
      }
      onSuccess();
    } catch (err: any) {
      setError(err.message || '操作失败');
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      if (!/^1[3-9]\d{9}$/.test(phone)) {
        throw new Error('手机号格式不正确');
      }
      if (phonePassword.length < 6) {
        throw new Error('密码至少6位');
      }
      if (isRegister && phonePassword !== phoneConfirm) {
        throw new Error('两次输入的密码不一致');
      }

      const user = isRegister
        ? await phoneRegister(phone, phonePassword)
        : await phoneLogin(phone, phonePassword);
      await refreshUser();

      if (user.wechat_openid) {
        onSuccess();
      } else {
        setPendingUser({ phone: user.phone || null, wechat_openid: user.wechat_openid || null });
      }
    } catch (err: any) {
      setError(err.message || '操作失败');
    } finally {
      setLoading(false);
    }
  };

  const openWechatPopup = (mode: 'login' | 'bind') => {
    fetch('/api/auth/wechat/qr')
      .then(res => res.json())
      .then(data => {
        if (!data.url) {
          setError(data.error || '微信登录未配置');
          return;
        }
        const width = 500;
        const height = 560;
        const left = window.screenX + (window.innerWidth - width) / 2;
        const top = window.screenY + (window.innerHeight - height) / 2;
        const popup = window.open(
          data.url,
          'wechatLogin',
          `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,location=no,status=no`
        );
        if (!popup) {
          setError('弹窗被浏览器拦截，请允许弹窗后重试');
          return;
        }

        // For binding mode, listen for postMessage from the popup.
        if (mode === 'bind') {
          const handler = (event: MessageEvent) => {
            if (event.origin !== window.location.origin) return;
            if (event.data?.type === 'WECHAT_BIND_CODE' && event.data.code) {
              window.removeEventListener('message', handler);
              if (qrCheckInterval) clearInterval(qrCheckInterval);
              handleBindWechat(event.data.code);
            }
          };
          window.addEventListener('message', handler);
        }

        // Detect when popup closes (login mode)
        const interval = setInterval(() => {
          if (popup.closed) {
            clearInterval(interval);
            setQrCheckInterval(null);
            if (mode === 'login') {
              // Tokens are set by the redirect URL in the popup; check auth state.
              refreshUser().then(() => {
                if (!localStorage.getItem('access_token')) return;
                // New WeChat users have no phone bound yet.
                fetchMe()
                  .then(me => {
                    if (!me.phone) {
                      setBindPhoneOpen(true);
                    } else {
                      onSuccess();
                    }
                  })
                  .catch(() => onSuccess());
              });
            }
          }
        }, 500);
        setQrCheckInterval(interval);
      })
      .catch((err: any) => setError(err.message || '打开微信登录失败'));
  };

  const handleWechatLogin = () => openWechatPopup('login');
  const handleBindWechatClick = () => openWechatPopup('bind');

  const handleBindPhone = async (e: React.FormEvent) => {
    e.preventDefault();
    setBindLoading(true);
    setError('');
    try {
      if (!/^1[3-9]\d{9}$/.test(bindPhoneValue)) {
        throw new Error('手机号格式不正确');
      }
      if (bindPhoneValue !== bindPhoneConfirm) {
        throw new Error('两次输入的手机号不一致');
      }
      await bindPhone(bindPhoneValue);
      await refreshUser();
      setBindPhoneOpen(false);
      onSuccess();
    } catch (err: any) {
      setError(err.message || '绑定手机号失败');
    } finally {
      setBindLoading(false);
    }
  };

  const handleBindWechat = async (code: string) => {
    setBindWechatLoading(true);
    setError('');
    try {
      await bindWechatCode(code);
      await refreshUser();
    } catch (err: any) {
      setError(err.message || '绑定微信失败');
    } finally {
      setBindWechatLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <header className="border-b border-border bg-background/95 backdrop-blur-[12px]">
        <div className="max-w-[1080px] mx-auto px-4 md:px-6 h-14 flex items-center gap-2">
          {onBack && (
            <button onClick={onBack} className="text-sm text-muted-foreground hover:text-foreground transition">
              ← 返回首页
            </button>
          )}
        </div>
      </header>
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-sm bg-card rounded-2xl border border-border shadow-sm p-8">
          <h1 className="text-2xl font-semibold text-card-foreground mb-2">登录 / 注册</h1>
          <p className="text-sm text-muted-foreground mb-6">选择方式继续使用 TingYue</p>

          {error && (
            <div className="mb-4 rounded-md border border-red-200 bg-red-50 text-red-600 px-4 py-3 text-sm flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              {error}
            </div>
          )}

          <Tabs defaultValue="phone" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="phone">手机号</TabsTrigger>
              <TabsTrigger value="email">邮箱</TabsTrigger>
            </TabsList>

            <TabsContent value="email">
              <form onSubmit={handleEmailSubmit} className="space-y-4">
                <div>
                  <Label className="text-xs text-muted-foreground block mb-1">邮箱</Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground block mb-1">密码</Label>
                  <Input
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="至少6位"
                    required
                    minLength={6}
                  />
                </div>
                <Button type="submit" className="w-full bg-[#533afd] hover:bg-[#412fd9]" disabled={loading}>
                  {loading ? '请稍候...' : isRegister ? '注册' : '登录'}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="phone">
              <form onSubmit={handlePhoneSubmit} className="space-y-4">
                <div>
                  <Label className="text-xs text-muted-foreground block mb-1">手机号</Label>
                  <Input
                    type="tel"
                    value={phone}
                    onChange={e => setPhone(e.target.value)}
                    placeholder="11位手机号"
                    required
                    maxLength={11}
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground block mb-1">密码</Label>
                  <Input
                    type="password"
                    value={phonePassword}
                    onChange={e => setPhonePassword(e.target.value)}
                    placeholder="至少6位"
                    required
                    minLength={6}
                  />
                </div>
                {isRegister && (
                  <div>
                    <Label className="text-xs text-muted-foreground block mb-1">确认密码</Label>
                    <Input
                      type="password"
                      value={phoneConfirm}
                      onChange={e => setPhoneConfirm(e.target.value)}
                      placeholder="再次输入密码"
                      required
                      minLength={6}
                    />
                  </div>
                )}
                <Button type="submit" className="w-full bg-[#533afd] hover:bg-[#412fd9]" disabled={loading}>
                  {loading ? '请稍候...' : isRegister ? '手机号注册' : '手机号登录'}
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          <div className="mt-4 text-center text-sm">
            <button
              onClick={() => setIsRegister(!isRegister)}
              className="text-[#533afd] hover:underline"
            >
              {isRegister ? '已有账号？去登录' : '没有账号？去注册'}
            </button>
          </div>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-card px-2 text-muted-foreground">或</span>
            </div>
          </div>

          <Button
            variant="outline"
            className="w-full"
            onClick={handleWechatLogin}
            disabled={loading}
          >
            <svg className="h-4 w-4 mr-2 text-green-600" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8.5 13a1 1 0 100-2 1 1 0 000 2zm5 0a1 1 0 100-2 1 1 0 000 2z" />
              <path d="M8.8 1.6C4.2 2.6.7 6.3.7 10.7c0 2.6 1.4 5 3.6 6.6-.2.8-.7 2.2-1.4 3.2 2 .2 4.2-.6 5.4-1.4 1 .3 2.1.5 3.2.5 5.8 0 10.5-3.7 10.5-8.3 0-4.5-4.7-8.2-10.5-8.2-.9 0-1.8.1-2.7.5zM19 14.2c0 3.2-3.6 5.8-8 5.8-1 0-1.9-.1-2.8-.4-1 .7-2.7 1.3-4.2 1.2.5-.8.9-1.9 1.1-2.6-1.8-1.3-2.9-3.2-2.9-5.3 0-3.9 3.8-7 8.5-7s8.5 3.1 8.5 7c0 .4 0 .8-.1 1.2l.2.1c.5.3.8.8.8 1.4 0 .9-.7 1.6-1.6 1.6-.4 0-.8-.2-1.1-.4-.3.4-.7.8-1.2 1.1.3.1.7.2 1.1.2.2 0 .4 0 .6-.1v.2z" />
            </svg>
            微信扫码登录
          </Button>

          {pendingUser && !pendingUser.wechat_openid && (
            <div className="mt-4 p-4 rounded-xl border border-border bg-muted/40">
              <p className="text-sm text-center mb-3">手机号已登录，建议绑定微信以便扫码登录</p>
              <Button
                variant="outline"
                className="w-full"
                onClick={handleBindWechatClick}
                disabled={bindWechatLoading}
              >
                <svg className="h-4 w-4 mr-2 text-green-600" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8.5 13a1 1 0 100-2 1 1 0 000 2zm5 0a1 1 0 100-2 1 1 0 000 2z" />
                  <path d="M8.8 1.6C4.2 2.6.7 6.3.7 10.7c0 2.6 1.4 5 3.6 6.6-.2.8-.7 2.2-1.4 3.2 2 .2 4.2-.6 5.4-1.4 1 .3 2.1.5 3.2.5 5.8 0 10.5-3.7 10.5-8.3 0-4.5-4.7-8.2-10.5-8.2-.9 0-1.8.1-2.7.5zM19 14.2c0 3.2-3.6 5.8-8 5.8-1 0-1.9-.1-2.8-.4-1 .7-2.7 1.3-4.2 1.2.5-.8.9-1.9 1.1-2.6-1.8-1.3-2.9-3.2-2.9-5.3 0-3.9 3.8-7 8.5-7s8.5 3.1 8.5 7c0 .4 0 .8-.1 1.2l.2.1c.5.3.8.8.8 1.4 0 .9-.7 1.6-1.6 1.6-.4 0-.8-.2-1.1-.4-.3.4-.7.8-1.2 1.1.3.1.7.2 1.1.2.2 0 .4 0 .6-.1v.2z" />
                </svg>
                {bindWechatLoading ? '绑定中...' : '绑定微信'}
              </Button>
              <button
                onClick={onSuccess}
                className="mt-2 w-full text-xs text-muted-foreground hover:text-foreground"
              >
                暂不绑定，直接进入 →
              </button>
            </div>
          )}

          <p className="mt-4 text-xs text-center text-muted-foreground">
            微信登录新用户需绑定手机号后方可进入应用。
          </p>
        </div>
      </main>

      {/* Bind phone dialog after WeChat login */}
      <Dialog open={bindPhoneOpen} onOpenChange={setBindPhoneOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>绑定手机号</DialogTitle>
            <DialogDescription>
              为了账号安全，请绑定您的手机号。
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleBindPhone} className="space-y-4 mt-4">
            <div>
              <Label className="text-xs text-muted-foreground block mb-1">手机号</Label>
              <Input
                type="tel"
                value={bindPhoneValue}
                onChange={e => setBindPhoneValue(e.target.value)}
                placeholder="11位手机号"
                required
                maxLength={11}
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground block mb-1">确认手机号</Label>
              <Input
                type="tel"
                value={bindPhoneConfirm}
                onChange={e => setBindPhoneConfirm(e.target.value)}
                placeholder="再次输入手机号"
                required
                maxLength={11}
              />
            </div>
            <Button type="submit" className="w-full bg-[#533afd] hover:bg-[#412fd9]" disabled={bindLoading}>
              {bindLoading ? '绑定中...' : '确认绑定'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
